import React from "react";
import { usd, usdSigned, monthLabel, ROLES, DIVISIONS, StaticNote } from "./ui";
import "./Dashboard.css";

// Forecast vs Budget by division AND role (matches the legacy page), plus a
// monthly budget-vs-actual table.
export default function BudgetForecast({ data }) {
  const { byDivision, budgetByDivRole, budget, context, month, totals } = data;

  // Forecast (Completed/Ongoing) per division/role.
  const fc = (div, role) => byDivision[div]?.[role]?.co || 0;
  const bg = (div, role) => budgetByDivRole?.[div]?.[role] ?? 0;

  const divRows = DIVISIONS.map((div) => {
    const roles = ROLES.map((role) => {
      const b = bg(div, role), f = fc(div, role);
      return { role, budget: b, forecast: f, gap: f - b, pctGoal: b > 0 ? Math.round((f / b) * 100) : null };
    });
    const bTot = roles.reduce((s, r) => s + r.budget, 0);
    const fTot = roles.reduce((s, r) => s + r.forecast, 0);
    return { div, roles, budget: bTot, forecast: fTot, gap: fTot - bTot, pctGoal: bTot > 0 ? Math.round((fTot / bTot) * 100) : null };
  });
  const grand = {
    budget: divRows.reduce((s, d) => s + d.budget, 0),
    forecast: divRows.reduce((s, d) => s + d.forecast, 0),
  };
  grand.gap = grand.forecast - grand.budget;
  grand.pctGoal = grand.budget > 0 ? Math.round((grand.forecast / grand.budget) * 100) : null;

  const gapCls = (g) => (g >= 0 ? "pos-text" : "neg-text");
  const money = (n) => (n ? usd(n) : "--");

  // Monthly budget vs actual/forecast.
  const budgets = context.budgets || {};
  const historical = context.historical || {};
  const mKeys = Array.from(new Set([...Object.keys(budgets), ...Object.keys(historical), month])).sort();

  return (
    <div className="dash">
      <section className="kpis kpis-3">
        <div className="kpi kpi-primary">
          <div className="kpi-label">{data.monthLabel} forecast</div>
          <div className="kpi-value">{usd(budget.forecast)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Budget</div>
          <div className="kpi-value">{budget.total != null ? usd(budget.total) : "—"}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Gap</div>
          <div className={"kpi-value " + gapCls(budget.variance || 0)}>{budget.variance != null ? usdSigned(budget.variance) : "—"}</div>
          <div className="kpi-sub">{grand.pctGoal != null ? grand.pctGoal + "% of goal" : ""}</div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head"><h2>Forecast vs Budget by Division &amp; Role — {data.monthLabel}</h2></div>
        <table className="grid">
          <thead><tr><th>Division / Role</th><th>Budget</th><th>Forecast</th><th>Gap</th><th>% of Goal</th></tr></thead>
          <tbody>
            {divRows.map((d) => (
              <React.Fragment key={d.div}>
                <tr className="subhead-row">
                  <td className="rowhead"><span className={"pill pill-" + d.div.toLowerCase()}>{d.div}</span></td>
                  <td className="strong">{money(d.budget)}</td>
                  <td className="strong">{money(d.forecast)}</td>
                  <td className={"strong " + gapCls(d.gap)}>{usdSigned(d.gap)}</td>
                  <td className={"strong " + (d.pctGoal >= 100 ? "pos-text" : "neg-text")}>{d.pctGoal != null ? d.pctGoal + "%" : "--"}</td>
                </tr>
                {d.roles.map((r) => (
                  <tr key={d.div + r.role}>
                    <td className="rowhead indent">{r.role}</td>
                    <td>{money(r.budget)}</td>
                    <td>{money(r.forecast)}</td>
                    <td className={gapCls(r.gap)}>{usdSigned(r.gap)}</td>
                    <td className={r.pctGoal == null ? "muted" : r.pctGoal >= 100 ? "pos-text" : "neg-text"}>{r.pctGoal != null ? r.pctGoal + "%" : "--"}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr className="total-row">
              <td className="rowhead">Grand Total</td>
              <td>{money(grand.budget)}</td>
              <td>{money(grand.forecast)}</td>
              <td className={gapCls(grand.gap)}>{usdSigned(grand.gap)}</td>
              <td>{grand.pctGoal != null ? grand.pctGoal + "%" : "--"}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="panel">
        <div className="panel-head"><h2>Budget vs Actual / Forecast by Month</h2></div>
        <table className="grid">
          <thead><tr><th>Month</th><th>Budget</th><th>Actual / Forecast</th><th>Variance</th><th>Attainment</th></tr></thead>
          <tbody>
            {mKeys.map((k) => {
              const b = budgets[k] ?? null;
              const actual = k === month ? totals.completedOngoing : (historical[k] ?? null);
              const variance = b != null && actual != null ? actual - b : null;
              return (
                <tr key={k} className={k === month ? "row-highlight" : ""}>
                  <td className="rowhead">{monthLabel(k)}{k === month ? " (forecast)" : ""}</td>
                  <td>{b != null ? usd(b) : "—"}</td>
                  <td>{actual != null ? usd(actual) : "—"}</td>
                  <td className={variance == null ? "muted" : variance >= 0 ? "pos-text" : "neg-text"}>{variance != null ? usdSigned(variance) : "—"}</td>
                  <td>{b && actual != null ? Math.round((actual / b) * 100) + "%" : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <StaticNote>Budget figures come from the 2026 budget workbook (per role and division). Only the current month's forecast is live from your upload; prior months show recorded actuals.</StaticNote>
    </div>
  );
}
